import React, { useState } from "react";
import {
  IconBoxMultipleFilled,
  IconCheck,
  IconEye,
  IconPencilFilled,
  IconPlus,
  IconTrashXFilled,
} from "@tabler/icons-react";
import { Button, FieldError, Input, Select, Textarea } from "../utils/ui";
import { api } from "../utils/api";
import { formatMoneda } from "../utils/format";

const BILLING_LABELS = { monthly: "Mensual", quarterly: "Trimestral", yearly: "Anual" };

function VistasSection({ permisos = [], cuentas = [], onChanged }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const usage = {};
  cuentas.forEach((cuenta) => {
    (cuenta.permission_ids || []).forEach((id) => {
      usage[id] = (usage[id] || 0) + 1;
    });
  });

  const startEdit = (p) => {
    setEditingId(p.id);
    setName(p.name);
    setSlug(p.slug);
    setDescription(p.description || "");
    setConfirmId(null);
    setError("");
  };

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setSlug("");
    setDescription("");
    setConfirmId(null);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { name: name.trim(), description: description.trim() || null };
      if (slug.trim()) payload.slug = slug.trim();
      if (editingId) {
        await api.updatePermiso(editingId, payload);
      } else {
        await api.createPermiso(payload);
      }
      const next = await onChanged();
      if (next) resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    setError("");
    try {
      await api.deletePermiso(id);
      const next = await onChanged();
      if (next) setConfirmId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-5">
      <div className="min-w-0 space-y-3 md:col-span-3">
        <p className="text-sm text-gray-400">
          Las vistas son las pantallas del POS. Cada cuenta verá solo las que le asignes; si agregas vistas
          nuevas aquí, estarán disponibles para cuentas nuevas y existentes de inmediato.
        </p>

        <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-2">
          {permisos.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-white/20 px-3 py-6 text-center text-sm text-gray-400">
              Aún no hay vistas registradas.
            </p>
          ) : (
            permisos.map((p, i) => {
              const usedBy = usage[p.id] || 0;
              return (
                <div
                  key={p.id}
                  className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 transition-colors ${
                    confirmId === p.id
                      ? "border-red-500/40 bg-red-500/10"
                      : "border-white/10 bg-white/5"
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-white">
                      <IconEye className="h-5 w-5" stroke={1.75} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-white">{p.name}</p>
                        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-gray-400">
                          {p.slug}
                        </span>
                        <span className="mx-1 text-xs text-gray-500">{i + 1}</span>
                      </div>
                      {p.description && (
                        <p className="mt-0.5 truncate text-xs text-gray-400">{p.description}</p>
                      )}
                    </div>
                  </div>
                  {confirmId === p.id ? (
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setConfirmId(null)}
                        className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/25"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(p.id)}
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-500 disabled:opacity-60"
                      >
                        <IconCheck className="h-4 w-4" stroke={2.2} />
                        {saving ? "Eliminando..." : "Eliminar"}
                      </button>
                    </div>
                  ) : (
                    <div className="flex shrink-0 items-center gap-2.5">
                      <button
                        type="button"
                        title={`Usada por ${usedBy} cuenta${usedBy !== 1 ? "s" : ""}`}
                        className="rounded-full bg-brand px-2.5 py-1 text-[11px] font-semibold text-white"
                      >
                        {usedBy} cuenta{usedBy !== 1 ? "s" : ""}
                      </button>
                      <button
                        type="button"
                        aria-label="Editar vista"
                        title="Editar vista"
                        onClick={() => startEdit(p)}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-black transition-transform hover:scale-105"
                      >
                        <IconPencilFilled className="h-4 w-4 text-black" />
                      </button>
                      <button
                        type="button"
                        aria-label="Eliminar vista"
                        title="Eliminar vista"
                        onClick={() => setConfirmId(p.id)}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-600 text-white transition-transform hover:scale-105"
                      >
                        <IconTrashXFilled className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="md:col-span-2">
        <form
          onSubmit={submit}
          className="rounded-2xl border border-white/10 bg-white/5 p-5 md:sticky md:top-0"
        >
          <div className="mb-4 flex items-center gap-2">
            {editingId ? (
              <IconPencilFilled className="h-4 w-4 text-white" />
            ) : (
              <IconPlus className="h-4 w-4 text-white" stroke={1.75} />
            )}
            <p className="text-sm font-semibold text-white">
              {editingId ? "Editar vista" : "Nueva vista"}
            </p>
          </div>
          <div className="space-y-4">
            <Input
              label="Nombre *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Reportes avanzados"
              required
            />
            <Input
              label="Slug (opcional)"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="Se genera automáticamente"
            />
            <Textarea
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Describe qué hace esta vista (opcional)"
            />
          </div>
          <FieldError message={error} />
          <div className="mt-4 flex justify-end gap-3 border-t border-white/10 pt-4">
            {editingId && (
              <Button type="button" variant="secondary" onClick={resetForm} disabled={saving}>
                Cancelar edición
              </Button>
            )}
            <Button type="submit" disabled={saving}>
              {saving ? "Guardando..." : editingId ? "Guardar cambios" : "Agregar vista"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PaquetesSection({ paquetes = [], onPaquetesChanged }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [billing, setBilling] = useState("monthly");
  const [editingId, setEditingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const locked = !editingId && paquetes.length >= 3;

  const startEdit = (p) => {
    setEditingId(p.id);
    setName(p.name);
    setSlug(p.slug);
    setDescription(p.description || "");
    setAmount(String(p.amount));
    setBilling(p.billing_cycle || "monthly");
    setConfirmId(null);
    setError("");
  };

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setSlug("");
    setDescription("");
    setAmount("");
    setBilling("monthly");
    setConfirmId(null);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        amount: parseFloat(amount),
        billing_cycle: billing,
      };
      if (slug.trim()) payload.slug = slug.trim();
      if (editingId) {
        await api.updatePaquete(editingId, payload);
      } else {
        await api.createPaquete(payload);
      }
      const next = await onPaquetesChanged();
      if (next) resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    setError("");
    try {
      await api.deletePaquete(id);
      const next = await onPaquetesChanged();
      if (next) setConfirmId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-5">
      <div className="min-w-0 space-y-3 md:col-span-3">
        <p className="text-sm text-gray-400">
          Paquetes de precio del servicio. Siempre se manejan hasta 3 paquetes; cámbialos, edita su precio
          o descripción y se aplicarán al momento de registrar pagos.
        </p>

        <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-2">
          {paquetes.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-white/20 px-3 py-6 text-center text-sm text-gray-400">
              Aún no hay paquetes registrados.
            </p>
          ) : (
            paquetes.map((p, i) => {
              const usedBy = p.usage_count || 0;
              return (
                <div
                  key={p.id}
                  className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 transition-colors ${
                    confirmId === p.id
                      ? "border-red-500/40 bg-red-500/10"
                      : "border-white/10 bg-white/5"
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand/20 text-brand">
                      <IconBoxMultipleFilled className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-white">{p.name}</p>
                        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-gray-400">
                          {p.slug}
                        </span>
                        <span className="text-sm font-bold text-white">
                          {formatMoneda(p.amount, p.currency)}
                        </span>
                        <span className="text-xs text-gray-400">
                          {BILLING_LABELS[p.billing_cycle] || p.billing_cycle}
                        </span>
                      </div>
                      {p.description && (
                        <p className="mt-0.5 truncate text-xs text-gray-400">{p.description}</p>
                      )}
                    </div>
                  </div>
                  {confirmId === p.id ? (
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setConfirmId(null)}
                        className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/25"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(p.id)}
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-500 disabled:opacity-60"
                      >
                        <IconCheck className="h-4 w-4" stroke={2.2} />
                        {saving ? "Eliminando..." : "Eliminar"}
                      </button>
                    </div>
                  ) : (
                    <div className="flex shrink-0 items-center gap-2.5">
                      <button
                        type="button"
                        title={`Usado por ${usedBy} sucursal${usedBy !== 1 ? "es" : ""}`}
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                          usedBy > 0 ? "bg-brand text-white" : "bg-white/10 text-gray-400"
                        }`}
                      >
                        {usedBy} sucursal{usedBy !== 1 ? "es" : ""}
                      </button>
                      <button
                        type="button"
                        aria-label="Editar paquete"
                        title="Editar paquete"
                        onClick={() => startEdit(p)}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-black transition-transform hover:scale-105"
                      >
                        <IconPencilFilled className="h-4 w-4 text-black" />
                      </button>
                      <button
                        type="button"
                        aria-label="Eliminar paquete"
                        title="Eliminar paquete"
                        onClick={() => setConfirmId(p.id)}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-600 text-white transition-transform hover:scale-105"
                      >
                        <IconTrashXFilled className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="md:col-span-2">
        <form
          onSubmit={submit}
          className="rounded-2xl border border-white/10 bg-white/5 p-5 md:sticky md:top-0"
        >
          <div className="mb-4 flex items-center gap-2">
            {editingId ? (
              <IconPencilFilled className="h-4 w-4 text-white" />
            ) : (
              <IconPlus className="h-4 w-4 text-white" stroke={1.75} />
            )}
            <p className="text-sm font-semibold text-white">
              {editingId ? "Editar paquete" : "Nuevo paquete"}
            </p>
          </div>
          <div className="space-y-4">
            <Input
              label="Nombre *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Plan Empresa"
              required
            />
            <Input
              label="Slug (opcional)"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="Se genera automáticamente"
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Precio *"
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Ej. 599"
                required
              />
              <Select label="Ciclo" value={billing} onChange={(e) => setBilling(e.target.value)}>
                <option value="monthly">Mensual</option>
                <option value="quarterly">Trimestral</option>
                <option value="yearly">Anual</option>
              </Select>
            </div>
            <Textarea
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Qué incluye el paquete (opcional)"
            />
          </div>
          <FieldError message={error} />
          <div className="mt-4 flex justify-end gap-3 border-t border-white/10 pt-4">
            {editingId && (
              <Button type="button" variant="secondary" onClick={resetForm} disabled={saving}>
                Cancelar edición
              </Button>
            )}
            {locked && !editingId ? (
              <p className="text-xs text-gray-400">Máximo 3 paquetes. Edita uno existente.</p>
            ) : (
              <Button type="submit" disabled={saving}>
                {saving ? "Guardando..." : editingId ? "Guardar cambios" : "Agregar paquete"}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ViewsManager({
  permisos = [],
  cuentas = [],
  paquetes = [],
  onChanged,
  onPaquetesChanged,
  onClose,
}) {
  const [tab, setTab] = useState("vistas");

  return (
    <div>
      <div className="mb-5 flex gap-2">
        <button
          type="button"
          onClick={() => setTab("vistas")}
          className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
            tab === "vistas"
              ? "bg-brand text-white"
              : "bg-white/10 text-white/60 hover:bg-white/15 hover:text-white"
          }`}
        >
          Vistas del POS
        </button>
        <button
          type="button"
          onClick={() => setTab("paquetes")}
          className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
            tab === "paquetes"
              ? "bg-brand text-white"
              : "bg-white/10 text-white/60 hover:bg-white/15 hover:text-white"
          }`}
        >
          Paquetes y precios
        </button>
      </div>

      {tab === "vistas" ? (
        <VistasSection permisos={permisos} cuentas={cuentas} onChanged={onChanged} />
      ) : (
        <PaquetesSection paquetes={paquetes} onPaquetesChanged={onPaquetesChanged} />
      )}
    </div>
  );
}